import React, { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_API_URL || "";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
};

export default function NotasCredito() {

  const [notasCredito, setNotasCredito] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const porPagina = 10;

  // Form state
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [motivo, setMotivo] = useState("Devolución de mercadería");
  const [detallesNC, setDetallesNC] = useState([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [ncRes, facRes] = await Promise.all([
        fetch(`${API}/api/notas-credito`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/facturas`, { headers: getAuthHeaders() })
      ]);
      const ncData = await ncRes.json();
      const facData = await facRes.json();
      setNotasCredito(Array.isArray(ncData) ? ncData : []);
      setFacturas(Array.isArray(facData) ? facData.filter(f => f.estadoSRI === "AUTORIZADO") : []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const seleccionarFactura = (claveAcceso) => {
    const fac = facturas.find(f => f.claveAcceso === claveAcceso);
    if (!fac) return;
    setFacturaSeleccionada(fac);
    
    // Extraer los detalles/items de la factura original
    if (fac.detalles && fac.detalles.length > 0) {
      setDetallesNC(fac.detalles.map((d, i) => ({
        codigo: d.codigo || String(i + 1).padStart(3, "0"),
        descripcion: d.nombre || d.descripcion || "",
        nombre: d.nombre || d.descripcion || "",
        cantidad: parseFloat(d.cantidad || 1),
        precioUnitario: parseFloat(d.precio || d.precioUnitario || 0),
        iva: d.iva || (parseFloat(fac.totales?.iva15 || 0) > 0 ? "15%" : "0%"),
        precioTotalSinImpuesto: parseFloat(d.cantidad || 1) * parseFloat(d.precio || d.precioUnitario || 0)
      })));
    } else {
      // Fallback si no hay detalles guardados (facturas antiguas)
      const subtotal = parseFloat(fac.totales?.subtotalSinImpuestos || fac.totales?.subtotal15 || fac.totales?.subtotal0 || 0);
      setDetallesNC([{
        codigo: "001", descripcion: "Devolución total", nombre: "Devolución total",
        cantidad: 1, precioUnitario: subtotal,
        iva: parseFloat(fac.totales?.iva15 || 0) > 0 ? "15%" : "0%",
        precioTotalSinImpuesto: subtotal
      }]);
    }
  };

  const actualizarDetalle = (idx, campo, valor) => {
    const copia = [...detallesNC];
    copia[idx] = { ...copia[idx], [campo]: valor };
    if (campo === "cantidad" || campo === "precioUnitario") {
      copia[idx].precioTotalSinImpuesto = parseFloat(copia[idx].cantidad || 0) * parseFloat(copia[idx].precioUnitario || 0);
    }
    setDetallesNC(copia);
  };

  const agregarDetalle = () => {
    setDetallesNC([...detallesNC, { codigo: "", descripcion: "", cantidad: 1, precioUnitario: 0, iva: "15%", precioTotalSinImpuesto: 0 }]);
  };

  const eliminarDetalle = (idx) => {
    setDetallesNC(detallesNC.filter((_, i) => i !== idx));
  };

  const calcularTotales = () => {
    let subtotal15 = 0, subtotal0 = 0;
    detallesNC.forEach(d => {
      const sub = parseFloat(d.precioTotalSinImpuesto || 0);
      if (d.iva === "15%") subtotal15 += sub; else subtotal0 += sub;
    });
    const iva15 = subtotal15 * 0.15;
    return { totalSinImpuestos: subtotal15 + subtotal0, subtotal15, subtotal0, iva15, valorTotal: subtotal15 + subtotal0 + iva15 };
  };

  const crearNotaCredito = async () => {
    if (!facturaSeleccionada) { setError("Seleccione una factura"); return; }
    if (detallesNC.length === 0) { setError("Agregue al menos un detalle"); return; }
    setLoading(true); setError(""); setMsg("");
    try {
      const totales = calcularTotales();
      const body = {
        facturaOriginal: {
          claveAcceso: facturaSeleccionada.claveAcceso,
          numeroComprobante: `${facturaSeleccionada.secuencial}`,
          secuencial: facturaSeleccionada.secuencial,
          fechaEmision: facturaSeleccionada.fechaEmision,
          establecimiento: "001", puntoEmision: "001"
        },
        cliente: facturaSeleccionada.cliente,
        detalles: detallesNC,
        totales,
        motivo
      };
      const res = await fetch(`${API}/api/notas-credito`, { method: "POST", headers: getAuthHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (data.success) {
        setMsg("Nota de crédito generada exitosamente. " + (data.message || ""));
        setShowForm(false); setFacturaSeleccionada(null); setDetallesNC([]);
        cargar();
      } else { setError(data.error || "Error al crear nota de crédito"); }
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const descargarPDF = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/api/notas-credito/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Error al descargar PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) { setError("Error al descargar PDF: " + e.message); }
  };

  const enviarEmail = async (nc) => {
    const email = nc.cliente?.email;
    if (!email) {
      setError("El cliente no tiene email registrado");
      return;
    }
    if (!window.confirm(`¿Enviar nota de crédito ${nc.numero} al correo ${email}?`)) return;
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/notas-credito/${nc.id}/enviar-email`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (data.success) {
        setMsg(`Email enviado a ${email}`);
      } else {
        setError(data.error || "Error al enviar email");
      }
    } catch (e) { setError("Error al enviar email: " + e.message); }
    setLoading(false);
  };

  const anularNC = async (nc) => {
    if (nc.estado_sri === "ANULADA") return;
    if (!window.confirm(`¿Anular la nota de crédito ${nc.numero}? Esta acción no se puede deshacer.`)) return;
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/notas-credito/${nc.id}/anular`, {
        method: "PUT",
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setMsg("Nota de crédito anulada correctamente");
        cargar();
      } else { setError(data.error || "Error al anular"); }
    } catch (e) { setError("Error al anular: " + e.message); }
    setLoading(false);
  };

  // Paginación
  const totalPaginas = Math.ceil(notasCredito.length / porPagina);
  const ncPaginadas = notasCredito.slice((paginaActual - 1) * porPagina, paginaActual * porPagina);

  const totales = calcularTotales();
  const estadoColor = (e) => e === "AUTORIZADO" ? "#4caf50" : e === "PENDIENTE" ? "#ff9800" : e === "ANULADA" ? "#9e9e9e" : "#f44336";

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>📄 Notas de Crédito</h2>
        <button onClick={() => setShowForm(!showForm)} style={btnPrimary}>{showForm ? "Cancelar" : "+ Nueva Nota de Crédito"}</button>
      </div>

      {msg && <div style={{ background: "#e8f5e9", padding: 12, borderRadius: 8, marginBottom: 16, color: "#2e7d32" }}>{msg}</div>}
      {error && <div style={{ background: "#ffebee", padding: 12, borderRadius: 8, marginBottom: 16, color: "#c62828" }}>{error}</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3>Nueva Nota de Crédito</h3>

          <label style={labelStyle}>Factura Original (solo autorizadas)</label>
          <select onChange={e => seleccionarFactura(e.target.value)} style={inputStyle} value={facturaSeleccionada?.claveAcceso || ""}>
            <option value="">-- Seleccionar factura --</option>
            {facturas.map(f => (
              <option key={f.claveAcceso} value={f.claveAcceso}>
                {f.secuencial} - {f.cliente?.nombre} - ${parseFloat(f.totales?.valorPagar || 0).toFixed(2)} ({f.fechaEmision})
              </option>
            ))}
          </select>

          {facturaSeleccionada && (
            <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 8, margin: "12px 0" }}>
              <strong>Cliente:</strong> {facturaSeleccionada.cliente?.nombre} | <strong>RUC:</strong> {facturaSeleccionada.cliente?.ruc} | <strong>Total:</strong> ${parseFloat(facturaSeleccionada.totales?.valorPagar || 0).toFixed(2)}
            </div>
          )}

          <label style={labelStyle}>Motivo</label>
          <input value={motivo} onChange={e => setMotivo(e.target.value)} style={inputStyle} placeholder="Motivo de la nota de crédito" />

          <h4>Detalles</h4>
          {detallesNC.map((d, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 2fr 80px 100px 80px 40px", gap: 8, marginBottom: 8 }}>
              <input value={d.codigo} onChange={e => actualizarDetalle(i, "codigo", e.target.value)} placeholder="Código" style={inputStyle} />
              <input value={d.descripcion} onChange={e => actualizarDetalle(i, "descripcion", e.target.value)} placeholder="Descripción" style={inputStyle} />
              <input type="number" value={d.cantidad} onChange={e => actualizarDetalle(i, "cantidad", parseFloat(e.target.value) || 0)} placeholder="Cant" style={inputStyle} />
              <input type="number" value={d.precioUnitario} onChange={e => actualizarDetalle(i, "precioUnitario", parseFloat(e.target.value) || 0)} placeholder="P.Unit" style={inputStyle} step="0.01" />
              <select value={d.iva} onChange={e => actualizarDetalle(i, "iva", e.target.value)} style={inputStyle}>
                <option value="15%">15%</option><option value="0%">0%</option>
              </select>
              <button onClick={() => eliminarDetalle(i)} style={{ ...btnDanger, padding: "4px 8px" }}>✕</button>
            </div>
          ))}
          <button onClick={agregarDetalle} style={{ ...btnSecondary, marginBottom: 16 }}>+ Agregar línea</button>

          <div style={{ background: "#e3f2fd", padding: 12, borderRadius: 8, marginBottom: 16 }}>
            <strong>Subtotal:</strong> ${totales.totalSinImpuestos.toFixed(2)} | <strong>IVA 15%:</strong> ${totales.iva15.toFixed(2)} | <strong>TOTAL NC:</strong> ${totales.valorTotal.toFixed(2)}
          </div>

          <button onClick={crearNotaCredito} disabled={loading} style={btnPrimary}>{loading ? "Procesando..." : "Generar y Enviar al SRI"}</button>
        </div>
      )}

      {/* Listado */}
      <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #e0e0e0" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f5f5f5" }}>
              <th style={thStyle}>Número</th><th style={thStyle}>Fecha</th><th style={thStyle}>Cliente</th>
              <th style={thStyle}>Factura Orig.</th><th style={thStyle}>Total</th><th style={thStyle}>Estado SRI</th><th style={thStyle}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {ncPaginadas.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 24, color: "#999" }}>No hay notas de crédito registradas</td></tr>
            ) : ncPaginadas.map(nc => (
              <tr key={nc.id} style={{ borderBottom: "1px solid #eee" }}>
                <td style={tdStyle}>{nc.numero}</td>
                <td style={tdStyle}>{nc.fecha_emision}</td>
                <td style={tdStyle}>{nc.cliente?.nombre}</td>
                <td style={tdStyle}>{nc.factura_original?.numeroComprobante}</td>
                <td style={tdStyle}>${parseFloat(nc.totales?.valorTotal || nc.totales?.importeTotal || 0).toFixed(2)}</td>
                <td style={tdStyle}><span style={{ background: estadoColor(nc.estado_sri), color: "#fff", padding: "2px 8px", borderRadius: 12, fontSize: 12 }}>{nc.estado_sri}</span></td>
                <td style={tdStyle}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => descargarPDF(nc.id)} style={btnIconPDF} title="Descargar PDF">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                    </button>
                    <button onClick={() => enviarEmail(nc)} style={btnIconEmail} title="Enviar por Email">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    </button>
                    {nc.estado_sri !== "ANULADA" && (
                      <button onClick={() => anularNC(nc)} style={btnIconAnular} title="Anular">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 16 }}>
          <button onClick={() => setPaginaActual(p => Math.max(1, p - 1))} disabled={paginaActual === 1} style={btnPag}>← Anterior</button>
          {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPaginaActual(p)} style={{ ...btnPag, ...(p === paginaActual ? { background: "#1976d2", color: "#fff" } : {}) }}>{p}</button>
          ))}
          <button onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))} disabled={paginaActual === totalPaginas} style={btnPag}>Siguiente →</button>
        </div>
      )}
      {notasCredito.length > 0 && (
        <div style={{ textAlign: "center", color: "#666", fontSize: 12, marginTop: 8 }}>
          Mostrando {(paginaActual - 1) * porPagina + 1}-{Math.min(paginaActual * porPagina, notasCredito.length)} de {notasCredito.length}
        </div>
      )}
    </div>
  );
}

const btnPrimary = { background: "#1976d2", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontSize: 14 };
const btnSecondary = { background: "#f5f5f5", color: "#333", border: "1px solid #ddd", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13 };
const btnDanger = { background: "#f44336", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" };
const btnIconPDF = { background: "#f44336", color: "#fff", border: "none", padding: "6px 8px", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center" };
const btnIconEmail = { background: "#1976d2", color: "#fff", border: "none", padding: "6px 8px", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center" };
const btnIconAnular = { background: "#ff9800", color: "#fff", border: "none", padding: "6px 8px", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center" };
const btnPag = { background: "#fff", border: "1px solid #ddd", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 13 };
const inputStyle = { width: "100%", padding: "8px 12px", border: "1px solid #ddd", borderRadius: 8, fontSize: 14, boxSizing: "border-box" };
const labelStyle = { display: "block", marginBottom: 4, marginTop: 12, fontWeight: 600, fontSize: 13 };
const thStyle = { padding: "10px 12px", textAlign: "left", fontSize: 13, fontWeight: 600 };
const tdStyle = { padding: "10px 12px", fontSize: 13 };
